import React, { useState, useEffect } from 'react'
import { X, Minus, Plus, HelpCircle } from 'lucide-react'
import generationsApi from '../../services/generationsApi'
import { motion, AnimatePresence } from 'framer-motion'
import { ToggleGroup, ToggleGroupItem } from "../ui/toggle-group"
import { Label } from "../ui/label"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip"
import { cn } from '../../lib/utils'
import { Spinner } from '../ui/spinner'

const ToggleButton = ({ 
  label, 
  isSelected, 
  onClick, 
  count, 
  onDecrement, 
  onIncrement, 
  showCount,
  totalCount,
  maxCount 
}) => {
  const isIncrementDisabled = totalCount >= maxCount
  const isDecrementDisabled = count <= 0

  return (
    <div className="flex items-center group">
      {showCount ? (
        <>
          <button
            className={`h-8 px-2 bg-gray-700 text-gray-300 rounded-l-md transition-colors
              ${isDecrementDisabled 
                ? 'opacity-50 cursor-not-allowed' 
                : 'hover:bg-gray-600 hover:text-white'}`}
            onClick={onDecrement}
            disabled={isDecrementDisabled}
            title={isDecrementDisabled 
              ? 'No photos of this style to remove' 
              : 'Decrease number of photos in this style'}
          >
            <Minus size={14} />
          </button>
          <div className="h-8 px-3 flex items-center bg-gray-700 text-gray-300">
            <span className="flex items-center gap-1.5">
              {label}
              <span className="text-sm text-gray-400">
                ({count})
              </span>
            </span>
          </div>
          <button
            className={`h-8 px-2 bg-gray-700 text-gray-300 rounded-r-md transition-colors
              ${isIncrementDisabled 
                ? 'opacity-50 cursor-not-allowed' 
                : 'hover:bg-gray-600 hover:text-white'}`}
            onClick={onIncrement}
            disabled={isIncrementDisabled}
            title={isIncrementDisabled 
              ? 'Maximum number of photos reached. Decrease other styles to add more of this style.' 
              : 'Increase number of photos in this style'}
          >
            <Plus size={14} />
          </button>
        </>
      ) : (
        <button
          className={`h-8 px-3 transition-all rounded-md ${
            isSelected 
              ? 'bg-blue-500 text-white' 
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
          onClick={onClick}
        >
          {label}
        </button>
      )}
    </div>
  )
}

const MODEL_CONFIGS = [
  {
    "id": "sdxl21",
    "name": "SDXL2.1",
    "displayName": "SDXL2.1",
    "releaseDate": "August 2022",
    "enabled": true,
    "category": "basic",
    "description": "SDXL2.1 is a versatile AI image generator that turns your text descriptions into detailed and realistic images. It's user-friendly and works offline, making it a great tool for bringing your ideas to life anywhere, anytime.",
  },
  {
    "id": "flux1s",
    "name": "flux.1 schnell",
    "displayName": "flux.1 schnell",
    "releaseDate": "August 2024",
    "enabled": false,
    "category": "basic",
    "description": "Flux.1 Schnell is all about speed and efficiency. It's perfect for quick tasks where you need fast results, making it ideal for creating simple images swiftly without the need for advanced features.",
  },
  {
    "id": "flux1d",
    "name": "flux.1 dev",
    "displayName": "flux.1 dev",
    "releaseDate": "August 2024",
    "enabled": true,
    "category": "pro",
    "description": "Flux.1 Dev offers high-quality images and advanced features, making it great for exploring creative ideas and pushing the boundaries of your projects. It's a good choice if you want to experiment with professional-level capabilities.",
  },
  {
    "id": "flux1p",
    "name": "flux.1 pro",
    "displayName": "flux.1 pro",
    "releaseDate": "August 2024",
    "enabled": false,
    "category": "pro",
    "description": "Flux.1 Pro is designed for those who need the highest quality and most detailed images. Packed with advanced features, it's perfect for professional projects that require precision and excellence.",
  },
  {
    "id": "flux11p",
    "name": "flux 1.1 pro",
    "displayName": "flux 1.1 pro",
    "releaseDate": "October 2024",
    "enabled": false,
    "category": "pro",
    "description": "Flux 1.1 Pro is the newest and most advanced version, offering faster image creation and higher-quality results than ever before. It's ideal for professionals who require top-tier images quickly and efficiently, making it perfect for demanding projects that need the best performance."
  }
]

const NewPhotoPanel = ({ onClose, onGenerate }) => {
  const [prompt, setPrompt] = useState('')
  const [orientation, setOrientation] = useState('')
  const [style, setStyle] = useState('')
  const [photoCount, setPhotoCount] = useState(1)
  const [tags, setTags] = useState([])
  const [tagInput, setTagInput] = useState('')
  const [isGenerating, setIsGenerating] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const [styleCounts, setStyleCounts] = useState({
    realistic: 0,
    artistic: 0,
    cinematic: 0,
    anime: 0
  })
  const [selectedModel, setSelectedModel] = useState('sdxl21')

  useEffect(() => {
    // Commenting out automatic style assignment logic
    /*
    if (photoCount === 1) {
      setStyleCounts({
        realistic: style === 'realistic' ? 1 : 0,
        artistic: style === 'artistic' ? 1 : 0,
        cinematic: style === 'cinematic' ? 1 : 0,
        anime: style === 'anime' ? 1 : 0
      })
    } else {
      const currentTotal = Object.values(styleCounts).reduce((sum, count) => sum + count, 0)
      if (currentTotal !== photoCount) {
        setStyleCounts(prev => ({
          ...prev,
          [style]: prev[style] + (photoCount - currentTotal)
        }))
      }
    }
    */
  }, [photoCount, style])

  const handleStyleChange = (newStyle) => {
    // Simply update the selected style without auto-assigning counts
    setStyle(newStyle)
  }

  const handleStyleCountChange = (styleKey, change) => {
    setStyleCounts(prev => {
      const newCount = Math.max(0, prev[styleKey] + change)
      const otherStylesCount = Object.entries(prev)
        .filter(([key]) => key !== styleKey)
        .reduce((sum, [, count]) => sum + count, 0)
      
      if (otherStylesCount + newCount > photoCount) {
        return prev
      }

      return { ...prev, [styleKey]: newCount }
    })
  }

  const handleAddTag = (e) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      setTags([...tags, tagInput.trim()])
      setTagInput('')
    }
  }

  const removeTag = (tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove))
  }

  const isFormValid = () => {
    // For single photo
    if (photoCount === 1) {
      return Boolean(prompt.trim() && orientation && style)
    }
    
    // For multiple photos
    const totalStyleCount = Object.values(styleCounts).reduce((sum, count) => sum + count, 0)
    return Boolean(
      prompt.trim() && 
      orientation && 
      totalStyleCount === photoCount
    )
  }

  const handleModelChange = (value) => {
    if (value) {
      setSelectedModel(value)
    }
  }

  const handleGenerate = async () => {
    if (!isFormValid()) {
      console.error('Form validation failed:', { 
        prompt, 
        orientation, 
        style,
        photoCount,
        styleCounts,
        selectedModel 
      })
      return
    }

    // Early validation for unsupported models
    if (selectedModel !== 'flux1d' && selectedModel !== 'sdxl21') {
      console.error('Unsupported model:', selectedModel)
      setIsGenerating(false)
      throw new Error(`Model ${selectedModel} is not currently supported. Only flux1d and sdxl21 are available.`)
      return
    }

    setIsGenerating(true)
    try {
      let generationPromises;
      
      if (photoCount === 1) {
        // Single photo case - use the selected style directly
        generationPromises = [
          generationsApi.create({
            prompt,
            orientation,
            style,
            model: selectedModel
          })
        ];
      } else {
        // Multiple photos case - use style counts
        generationPromises = Object.entries(styleCounts).flatMap(([style, count]) =>
          Array(count).fill().map(() =>
            generationsApi.create({
              prompt,
              orientation,
              style,
              model: selectedModel
            })
          )
        );
      }
      
      const generationResponses = await Promise.all(generationPromises)
      
      const pendingGenerations = generationResponses.map(gen => ({
        ...gen,
        id: gen._id,
        prompt,
        orientation,
        style: gen.style,
        model: selectedModel,
        status: 'processing',
        createdAt: gen.startedAt || new Date().toISOString()
      }))

      console.log('Created generations:', pendingGenerations)
      
      setIsClosing(true)
      
      setTimeout(() => {
        onGenerate(pendingGenerations)
        onClose()
      }, 300)
    } catch (error) {
      console.error('Failed to start generation:', error)
      setIsGenerating(false)
      throw error
    }
  }

  const totalStyleCount = Object.values(styleCounts).reduce((sum, count) => sum + count, 0)

  const ModelTooltip = ({ model }) => (
    <TooltipContent side="top" align="center" className="max-w-[280px] p-4 bg-gray-800 border border-gray-700 shadow-xl">
      <div className="space-y-2">
        <p className="font-medium text-white">{model.displayName}</p>
        <p className="text-sm text-gray-400">Released: {model.releaseDate}</p>
        <p className="text-sm text-gray-300">{model.description}</p>
      </div>
    </TooltipContent>
  )

  return (
    <AnimatePresence>
      {!isClosing && (
        <motion.div
          className="fixed bottom-28 left-4 z-30 max-w-xl"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 50 }}
          transition={{ duration: 0.3 }}
        >
          <div className="bg-gray-900/95 rounded-2xl shadow-xl border border-gray-800 overflow-hidden">
            <div className="p-5">
              {/* Header */}
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-white">Take a New Photo</h2>
                <button 
                  onClick={onClose}
                  className="p-1.5 hover:bg-gray-700 rounded-full transition-colors"
                  title="Close panel"
                >
                  <X className="text-gray-400 hover:text-white" size={20} />
                </button>
              </div>

              {/* Model Selection */}
              <div className="mb-6">
                <label className="block text-white mb-2 text-sm font-medium">Model</label>
                <div className="flex flex-wrap gap-2">
                  {MODEL_CONFIGS
                    .sort((a, b) => {
                      // First sort by enabled status (enabled models first)
                      if (a.enabled !== b.enabled) {
                        return a.enabled ? -1 : 1;
                      }
                      // Then sort by category (basic before pro)
                      if (a.category !== b.category) {
                        return a.category === 'basic' ? -1 : 1;
                      }
                      // Finally sort by display name
                      return a.displayName.localeCompare(b.displayName);
                    })
                    .map(model => (
                    <TooltipProvider key={model.id}>
                      <Tooltip delayDuration={300}>
                        <TooltipTrigger asChild>
                          <div className="relative group">
                            <button
                              className={`h-8 px-3 transition-all rounded-md ${
                                !model.enabled
                                  ? 'bg-gray-800 text-gray-500 cursor-not-allowed opacity-50'
                                  : selectedModel === model.id
                                  ? 'bg-blue-500 text-white'
                                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                              }`}
                              onClick={() => model.enabled && handleModelChange(model.id)}
                              disabled={!model.enabled}
                            >
                              {model.displayName}
                            </button>
                            {!model.enabled && (
                              <div className="absolute -bottom-5 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                                <span className="text-[10px] text-gray-500 font-medium tracking-wide uppercase">
                                  Coming Soon
                                </span>
                              </div>
                            )}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent 
                          side="top" 
                          align="center" 
                          className="max-w-[280px] p-4 bg-gray-800 border border-gray-700 shadow-xl"
                        >
                          <div className="space-y-2">
                            <p className="font-medium text-white">{model.displayName}</p>
                            <p className="text-sm text-gray-400">Released: {model.releaseDate}</p>
                            <p className="text-sm text-gray-300">{model.description}</p>
                            <div className="mt-3 space-y-1">
                              <p className="text-sm text-gray-300">
                                <span className="font-medium text-gray-200">Pros:</span> {model.pros}
                              </p>
                              <p className="text-sm text-gray-300">
                                <span className="font-medium text-gray-200">Cons:</span> {model.cons}
                              </p>
                            </div>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ))}
                </div>
              </div>

              {/* Photo Count */}
              <div className="mb-6">
                <label className="block text-white mb-2 text-sm font-medium">Number of Photos</label>
                <div className="flex flex-wrap gap-2">
                  {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
                    <ToggleButton
                      key={num}
                      label={num.toString()}
                      isSelected={photoCount === num}
                      onClick={() => setPhotoCount(num)}
                    />
                  ))}
                </div>
              </div>

              {/* Prompt */}
              <div className="mb-6">
                <label className="block text-white mb-2 text-sm font-medium">
                  Describe your photo{photoCount > 1 ? 's' : ''}
                </label>
                <textarea
                  className="w-full p-3 bg-gray-800 rounded-md border border-gray-700 text-white resize-none 
                    text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                  rows="3"
                  placeholder={`Describe what you want to generate${photoCount > 1 ? ' (all photos will use this prompt)' : ''}...`}
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                />
              </div>

              {/* Orientation */}
              <div className="mb-6">
                <label className="block text-white mb-2 text-sm font-medium">Orientation</label>
                <div className="flex gap-2">
                  <ToggleButton 
                    label="Square" 
                    isSelected={orientation === 'square'}
                    onClick={() => setOrientation('square')}
                  />
                  <ToggleButton 
                    label="Portrait" 
                    isSelected={orientation === 'portrait'}
                    onClick={() => setOrientation('portrait')}
                  />
                  <ToggleButton 
                    label="Landscape" 
                    isSelected={orientation === 'landscape'}
                    onClick={() => setOrientation('landscape')}
                  />
                </div>
              </div>

              {/* Style */}
              <div className="mb-6">
                <label className="block text-white mb-2 text-sm font-medium">
                  Style {photoCount > 1 && (
                    <span className={`text-xs ml-2 ${
                      totalStyleCount < photoCount ? 'text-yellow-400' : 'text-gray-400'
                    }`}>
                      ({totalStyleCount}/{photoCount} photos assigned - 
                      {totalStyleCount < photoCount 
                        ? ` ${photoCount - totalStyleCount} more to assign` 
                        : ' all photos assigned'}
                      )
                    </span>
                  )}
                </label>
                <div className="flex flex-wrap gap-2">
                  {['realistic', 'artistic', 'cinematic', 'anime'].map(styleOption => (
                    <ToggleButton 
                      key={styleOption}
                      label={styleOption.charAt(0).toUpperCase() + styleOption.slice(1)}
                      isSelected={style === styleOption}
                      onClick={() => handleStyleChange(styleOption)}
                      count={styleCounts[styleOption]}
                      onDecrement={() => handleStyleCountChange(styleOption, -1)}
                      onIncrement={() => handleStyleCountChange(styleOption, 1)}
                      showCount={photoCount > 1}
                      totalCount={totalStyleCount}
                      maxCount={photoCount}
                    />
                  ))}
                </div>
              </div>

              {/* Tags - Coming Soon */}
              {/* <div className="mb-6">
                <label className="block text-white mb-2 text-sm font-medium">
                  Tags <span className="text-xs text-gray-400">(Coming Soon)</span>
                </label>
                <div className="p-3 bg-gray-800/50 rounded-md border border-gray-700 text-gray-400 text-sm">
                  Tag support is coming soon! You'll be able to organize and find your photos easily.
                </div>
              </div> */}

              {/* Generate Button - Updated disabled logic */}
              <button
                className="w-full bg-blue-500 text-white py-2.5 rounded-md hover:bg-blue-600 transition-colors 
                  disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                onClick={handleGenerate}
                disabled={isGenerating || !isFormValid()}
                title={
                  !prompt.trim() ? 'Please enter a prompt' :
                  !orientation ? 'Please select an orientation' :
                  photoCount === 1 && !style ? 'Please select a style' :
                  photoCount > 1 && totalStyleCount !== photoCount ? 'Please assign all photos to styles' :
                  isGenerating ? 'Generation in progress...' :
                  'Generate photos'
                }
              >
                {isGenerating ? (
                  <>
                    <Spinner className="mr-2" />
                    <span>Generating {photoCount} Photo{photoCount > 1 ? 's' : ''}...</span>
                  </>
                ) : (
                  <span>Generate {photoCount} Photo{photoCount > 1 ? 's' : ''}</span>
                )}
              </button>
            </div>
          </div>
          
          {/* Chat bubble arrow */}
          <motion.div 
            className="absolute -bottom-3 left-8 w-6 h-6 overflow-hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div 
              className="w-3 h-3 bg-gray-900/95 border-b border-r border-gray-800 rotate-45 transform origin-center translate-y-1.5"
              style={{ marginLeft: '6px' }}
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default NewPhotoPanel 